import React from "react";
import styled from "styled-components";

import { UploadRowError } from "../../api/LCSModels";

import Color from "../../resources/colors";
import ErrorIcon from "../../resources/images/error-icon.png";
import WarningIcon from "../../resources/images/warning-icon.png";
import CloseIcon from "../../resources/images/close-icon.png";

import { HFlex, VFlex, IconButton } from "../../pandora/styled";
import { FadeAnimation } from "../../pandora/animations";


const Container = styled(VFlex)<{ borderColor: string }>`
    margin-top: 20px;
    padding: 15px;
    box-sizing: border-box;

    border: 1px solid ${props => props.borderColor};
    border-radius: 5px;
    background-color: ${Color.extraDarkGrey};

    animation: ${FadeAnimation} 0.5s ease 1;

    .icon {
        width: 20px;
        height: 20px;
        margin-right: 10px;
    }

    .footer {
        margin-top: 10px;
        font-weight: normal;
        color: ${Color.extraLightGrey};
    }
`

const Table = styled.div`
    display: grid;
    grid-template-columns: auto auto auto 1fr;
    max-height: 400px;
    margin-top: 15px;
    overflow-y: auto;

    h5 {
        padding: 5px 10px;
        font-weight: normal;
        border-bottom: 1px solid ${Color.lightGrey};
        word-break: break-word;
    }

    .header {
        position: sticky;
        top: 0;
        font-weight: bold;
        background-color: ${Color.lightGrey};
    }
`

interface UploadErrorPanelProps {
    message: string;
    rows: UploadRowError[];
    totalRows: number;
    isWarning?: boolean;
    onClose: () => void;
}

/**
 * Lists the rows of an uploaded file that the API rejected, or accepted with warnings.
 */
const UploadErrorPanel = (props: UploadErrorPanelProps): React.ReactElement => {

    const { message, rows, totalRows, isWarning } = props;
    const hiddenRows = totalRows - rows.length;

    const displayValue = (value: string | null): string => {
        if (value === null) return "–";
        return value === "" ? "(empty)" : value;
    }

    return (
        <Container borderColor={isWarning ? Color.yellow : Color.red}>
            <HFlex style={{justifyContent: "space-between", alignItems: "center"}}>
                <HFlex style={{alignItems: "center"}}>
                    <img className="icon" src={isWarning ? WarningIcon : ErrorIcon} />
                    <h4>{message}</h4>
                </HFlex>
                <IconButton
                    color=      {Color.lightGrey}
                    hoverColor= {Color.extraLightGrey}
                    icon=       {CloseIcon}
                    onClick=    {props.onClose}
                    style=      {{marginRight: 0}}
                >Dismiss</IconButton>
            </HFlex>
            {rows.length > 0 &&
                <Table>
                    <h5 className="header">Row</h5>
                    <h5 className="header">Column</h5>
                    <h5 className="header">Value</h5>
                    <h5 className="header">Problem</h5>
                    {rows.map((row, index) =>
                        <React.Fragment key={index}>
                            <h5>{row.row ?? "–"}</h5>
                            <h5>{row.column ?? "–"}</h5>
                            <h5>{displayValue(row.value)}</h5>
                            <h5>{row.message}</h5>
                        </React.Fragment>
                    )}
                </Table>
            }
            {hiddenRows > 0 &&
                <h5 className="footer">{`…and ${hiddenRows} more`}</h5>
            }
        </Container>
    )
}

export default UploadErrorPanel;
